/**
 * Singapura — AR Verification of Data preset (my_preset / sg / singapura) — 자체완결 모듈.
 *
 *   my_preset/sg/singapura/
 *     verification/         문서 컴포넌트 + 반복부 config (cdd 는 aura/agm 재사용)
 *     components/PreSetSingapuraVerificationPanel.vue
 *     chatflows/singapuraVerificationFlow.ts
 *     utils/normalizeSingapuraVerificationForWord.ts
 *
 * 배선: main.ts 의 `import '@/features/startTask/my_preset/sg/singapura'` 로 로드 시 자동 등록.
 *   매칭 = task code CSI001, 또는 My Preset(클라이언트 커스텀)에서 온 'verification' 선택 (priority 55).
 */
import { registerPanel } from '@/features/startTask/preset_task/shared/preset/panels/panelRegistry';
import { registerChatFlow } from '@/features/startTask/preset_task/shared/preset/chatflows/chatFlowRegistry';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import PreSetSingapuraVerificationPanel from './components/PreSetSingapuraVerificationPanel.vue';
import { singapuraVerificationFlow } from './chatflows/singapuraVerificationFlow';
import PreSetSingapuraActiveDeclarationPanel from './components/PreSetSingapuraActiveDeclarationPanel.vue';
import { singapuraActiveDeclarationFlow } from './chatflows/singapuraActiveDeclarationFlow';
import PreSetSingapuraAgmPanel from './components/PreSetSingapuraAgmPanel.vue';
import { singapuraAgmFlow } from './chatflows/singapuraAgmFlow';
import PreSetSingapuraRegistersPanel from './components/PreSetSingapuraRegistersPanel.vue';
import { singapuraRegistersFlow } from './chatflows/singapuraRegistersFlow';

export const SINGAPURA_VERIFICATION_TASK_CODES: string[] = ['CSI001'];
export const SINGAPURA_ACTIVE_DECLARATION_TASK_CODES: string[] = ['CSI002'];
export const SINGAPURA_AGM_TASK_CODES: string[] = ['CSI003'];
export const SINGAPURA_REGISTERS_TASK_CODES: string[] = ['CSI004'];

export function matchesSingapuraVerification(sel: PreSetSelection): boolean {
  // 코드 전용 매칭(CSI001). 이름 fallback 제거 — 고객사 접두어 코드가 유일 라우팅 키.
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  return SINGAPURA_VERIFICATION_TASK_CODES.map((c) => c.toUpperCase()).includes(code);
}
export function matchesSingapuraActiveDeclaration(sel: PreSetSelection): boolean {
  // 코드 전용 매칭(CSI002).
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  return SINGAPURA_ACTIVE_DECLARATION_TASK_CODES.map((c) => c.toUpperCase()).includes(code);
}
export function matchesSingapuraAgm(sel: PreSetSelection): boolean {
  // 코드 전용 매칭(CSI003).
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  return SINGAPURA_AGM_TASK_CODES.map((c) => c.toUpperCase()).includes(code);
}
export function matchesSingapuraRegisters(sel: PreSetSelection): boolean {
  // 코드 전용 매칭(CSI004) — ROND/RONS/RORC 통합.
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  return SINGAPURA_REGISTERS_TASK_CODES.map((c) => c.toUpperCase()).includes(code);
}

export function registerSingapuraVerificationPreset(): void {
  registerChatFlow(singapuraVerificationFlow);
  registerPanel({
    id: 'singapura-verification',
    priority: 55,
    match: (sel) => matchesSingapuraVerification(sel),
    component: PreSetSingapuraVerificationPanel,
    meta: { docType: 'SG_AR_VERIFICATION', studio: true },
  });
}
export function registerSingapuraActiveDeclarationPreset(): void {
  registerChatFlow(singapuraActiveDeclarationFlow);
  registerPanel({
    id: 'singapura-active-declaration',
    priority: 55,
    match: (sel) => matchesSingapuraActiveDeclaration(sel),
    component: PreSetSingapuraActiveDeclarationPanel,
    meta: { docType: 'SG_AR_ACTIVE_DECL', studio: true },
  });
}

export function registerSingapuraAgmPreset(): void {
  registerChatFlow(singapuraAgmFlow);
  registerPanel({
    id: 'singapura-agm',
    priority: 55,
    match: (sel) => matchesSingapuraAgm(sel),
    component: PreSetSingapuraAgmPanel,
    meta: { docType: 'SG_AR_AGM_EXEMPT', studio: true },
  });
}

export function registerSingapuraRegistersPreset(): void {
  registerChatFlow(singapuraRegistersFlow);
  registerPanel({
    id: 'singapura-registers',
    priority: 55,
    match: (sel) => matchesSingapuraRegisters(sel),
    component: PreSetSingapuraRegistersPanel,
    meta: { docType: 'SG_AR_REGISTERS', studio: true },
  });
}

registerSingapuraVerificationPreset();
registerSingapuraActiveDeclarationPreset();
registerSingapuraAgmPreset();
registerSingapuraRegistersPreset();
